/**
 * ImageFieldType — obrazek. Storage: PG TEXT (klucz MinIO).
 *
 * renderInput: wybór pliku obrazka (JPEG/PNG/GIF/WebP) → upload → onChange(key).
 *   Pokazuje thumbnail podglądu zaraz po wyborze pliku (data URL przed uploadem,
 *   potem key).
 * renderCell: pokazuje nazwę pliku (ostatni segment klucza MinIO).
 */

import { useRef, useState } from 'react';
import { Image as ImageIcon, X } from 'lucide-react';

import { api } from '@/lib/api';

import type { FieldInputProps, FieldTypeDefinition } from '../FieldRegistry';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

function ImageInput({ field, value, onChange, disabled }: FieldInputProps) {
  const currentKey = typeof value === 'string' ? value : '';
  const filename = currentKey ? (currentKey.split('/').pop() ?? currentKey) : '';

  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Dozwolone formaty: JPEG, PNG, GIF, WebP.');
      return;
    }

    setError(null);
    // Pokaż podgląd natychmiast
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldSlug', field.slug);

      const res = await api.post<{ key: string; filename: string }>(
        `/modules/${field.moduleId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      onChange(res.data.key);
      setPreview(null); // key is now in value
    } catch {
      setError('Nie udało się przesłać obrazka. Spróbuj ponownie.');
      setPreview(null);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleClear = () => {
    onChange(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const showKey = currentKey && !preview;

  return (
    <div data-testid={`field-${field.slug}-input`} className="space-y-1">
      {showKey || preview ? (
        <div className="relative inline-block">
          {preview ? (
            <img
              src={preview}
              alt="Podgląd"
              className="h-24 w-24 rounded-md border border-border-primary object-cover"
            />
          ) : (
            <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm">
              <ImageIcon className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
              <span className="min-w-0 max-w-[200px] truncate text-text-secondary" title={filename}>
                {filename}
              </span>
            </div>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-error-500 text-white shadow-sm hover:bg-error-600"
              aria-label="Usuń obrazek"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ) : (
        <label
          className={
            'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed px-4 py-4 text-sm transition ' +
            (disabled
              ? 'cursor-not-allowed border-border-primary text-text-muted'
              : 'border-border-primary text-text-secondary hover:border-accent-500 hover:text-accent-500')
          }
        >
          <ImageIcon className="h-6 w-6" aria-hidden="true" />
          {uploading ? (
            <span className="text-text-muted">Przesyłanie…</span>
          ) : (
            <span className="text-xs">{disabled ? '— brak obrazka —' : 'Wybierz obrazek…'}</span>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="sr-only"
            disabled={disabled || uploading}
            onChange={handleFileChange}
          />
        </label>
      )}
      {error && <p className="text-xs text-error-500">{error}</p>}
    </div>
  );
}

export const ImageFieldType: FieldTypeDefinition = {
  type: 'image',
  renderInput: (props) => <ImageInput {...props} />,
  renderCell: (_field, value) => {
    if (typeof value !== 'string' || !value) return '';
    return value.split('/').pop() ?? value;
  },
  defaultValue: null,
};
